import { useState, useEffect, useCallback } from 'react';
import { categoriesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Plus, Pencil, Trash2, Check, X as XIcon } from 'lucide-react';

const CATEGORY_TYPES = [
  { id: 'ticket', label: 'Tickets', description: 'Used in the ticketing system, search filters, and dashboards.' },
  { id: 'kb', label: 'Knowledge Base', description: 'Used to organize knowledge base articles.' },
  { id: 'documentation', label: 'Documentation', description: 'Used to organize client / clinic documentation entries.' },
  { id: 'antivirus', label: 'Antivirus', description: 'Antivirus brands a customer can be tagged with (shown beside the client name in Customers).' },
  { id: 'backup', label: 'Backup', description: 'Backup product types tracked per customer (Datto, Local, Acronis, …).' },
];

function TypePanel({ type, label, description }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await categoriesAPI.list({ type, include_disabled: true });
      setItems(res.data || []);
    } catch (err) {
      console.error('Failed to load categories', err);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      setCreating(true);
      await categoriesAPI.create({ type, name: newName.trim() });
      setNewName('');
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create category');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveRename = async (id) => {
    if (!editName.trim()) return;
    try {
      await categoriesAPI.update(id, { name: editName.trim() });
      setEditingId(null);
      setEditName('');
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to rename');
    }
  };

  const handleToggle = async (item) => {
    try {
      await categoriesAPI.update(item.id, { enabled: !item.enabled });
      await load();
    } catch (err) {
      alert('Failed to update');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete category "${item.name}"? This cannot be undone.`)) return;
    try {
      await categoriesAPI.delete(item.id);
      await load();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">{description}</p>

      <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2" data-testid={`add-cat-form-${type}`}>
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New category name"
          className="bg-white border-slate-200 h-11"
          data-testid={`add-cat-input-${type}`}
          aria-label={`Add ${label} category`}
        />
        <Button type="submit" className="rounded-full bg-cyan-500 hover:bg-cyan-600 h-11 px-5" disabled={creating || !newName.trim()} data-testid={`add-cat-btn-${type}`}>
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" />Add</>}
        </Button>
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-cyan-500" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">No categories yet — add one above.</p>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl bg-white">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-3 p-3" data-testid={`cat-row-${item.id}`}>
              {editingId === item.id ? (
                <>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveRename(item.id); } }}
                    className="bg-white border-slate-200 h-9 flex-1"
                    autoFocus
                    data-testid={`cat-rename-input-${item.id}`}
                  />
                  <Button size="icon" variant="ghost" className="h-9 w-9 text-emerald-600 hover:bg-emerald-50" onClick={() => handleSaveRename(item.id)} aria-label="Save rename">
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-500 hover:bg-slate-100" onClick={() => { setEditingId(null); setEditName(''); }} aria-label="Cancel">
                    <XIcon className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <span className={`flex-1 text-sm ${item.enabled ? 'text-slate-800 font-medium' : 'text-slate-400 line-through'}`}>
                    {item.name}
                  </span>
                  {!item.enabled && <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[10px]">Disabled</Badge>}
                  <Switch
                    checked={item.enabled}
                    onCheckedChange={() => handleToggle(item)}
                    aria-label={`${item.enabled ? 'Disable' : 'Enable'} ${item.name}`}
                    data-testid={`cat-toggle-${item.id}`}
                  />
                  <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-600 hover:bg-slate-100" onClick={() => { setEditingId(item.id); setEditName(item.name); }} aria-label="Rename" data-testid={`cat-rename-btn-${item.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9 text-red-600 hover:bg-red-50" onClick={() => handleDelete(item)} aria-label="Delete" data-testid={`cat-delete-btn-${item.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function CategoriesSettings() {
  return (
    <Card className="bg-white border-slate-200">
      <CardHeader>
        <CardTitle className="text-slate-800">Category Management</CardTitle>
        <p className="text-sm text-slate-500">Add, rename, disable, or delete categories used across tickets, knowledge base, and documentation.</p>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="ticket" className="w-full">
          <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1 mb-4 w-full h-auto">
            {CATEGORY_TYPES.map((t) => (
              <TabsTrigger key={t.id} value={t.id} data-testid={`cat-type-tab-${t.id}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
          {CATEGORY_TYPES.map((t) => (
            <TabsContent key={t.id} value={t.id} className="mt-2">
              <TypePanel type={t.id} label={t.label} description={t.description} />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
